import {
  Linking,
  ScrollView,
  StyleSheet,
  Text,
  View,
  useWindowDimensions,
} from 'react-native';
import React, { FC, useCallback, useEffect, useMemo, useState } from 'react';
import { NoticeDetailScreenProps } from './types';
import PageTitle from '../../components/PageTitle';
import { STYLE_VARS } from '../../constants/style-vars';
import { Button } from '../../components/Button';
import RenderHTML from 'react-native-render-html';
import { Footer } from '../../components/Footer';
import { FONTS } from '../../constants';
import { useIsDesktop } from '../../hooks/useIsDeskTop';
import NoticeLayout from '../../WebLayout/NoticeLayout/NoticeLayout';
import dayjs from 'dayjs';
import { INotice } from '../../types/notices.types';
import { NoticesApi } from '../../services/api';
import { Loading } from '../../components';
import Pdf from 'react-native-pdf';
import { IS_ANDROID, IS_WEB } from '../../constants/platform';

const NoticeDetail: FC<NoticeDetailScreenProps> = ({ navigation, route }) => {
  const noticeId = route.params.id;
  const isDesktop = useIsDesktop();
  const { width } = useWindowDimensions();

  const [notice, setNotice] = useState<INotice>();
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isScrolling, setIsScrolling] = useState<boolean>(false);

  const setIsScrollingTrue = () => setIsScrolling(true);
  const setIsScrollingFalse = () => setIsScrolling(false);

  const isScrollingEnabled = useMemo(() => {
    return IS_ANDROID ? !isScrolling : true;
  }, [isScrolling]);

  const getNotice = useCallback(async (id: number) => {
    try {
      setIsLoading(true);
      const res = await NoticesApi.getNotice(id);
      setNotice(res.data);
    } catch (error: any) {
      console.log(error);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    getNotice(noticeId);
  }, [noticeId, getNotice]);

  if (isDesktop()) {
    return (
      <NoticeLayout haveGoBackButton={true}>
        {isLoading === false && notice ? (
          <View
            style={[
              styles.borderDarkGrey,
              {
                paddingHorizontal: 20,
                paddingVertical: 30,
              },
            ]}
          >
            <Text style={[styles.titleText, { fontSize: 24 }]}>
              {notice?.title}
            </Text>
            <View style={styles.titleSpan} />
            <Text style={styles.dateText}>
              {dayjs(new Date(notice.date * 1000)).format('DD-MM-YYYY')}
            </Text>
            <Text style={styles.contentText}>
              <RenderHTML
                contentWidth={width / 2.2}
                source={{ html: notice.content }}
                tagsStyles={{
                  a: {
                    fontSize: 16,
                  },
                }}
              />
            </Text>
            {notice.document && (
              <>
                <View
                  style={{
                    marginBottom: 20,
                  }}
                >
                  <iframe
                    src={`${notice.document.url}#view=FitH&toolbar=0&background=0&statusbar=0&messages=0&navpanes=0`}
                    style={{
                      height: '120vh',
                    }}
                    frameBorder={0}
                    typeof={'application/pdf'}
                  ></iframe>
                </View>
                <View
                  style={{
                    flexDirection: 'row',
                    alignItems: 'center',
                  }}
                >
                  <Button
                    style={STYLE_VARS.blueButton}
                    text="DOWNLOAD PDF"
                    onPress={() => {
                      Linking.openURL(notice.document.url);
                    }}
                  />
                </View>
              </>
            )}
          </View>
        ) : isLoading === true ? (
          <View style={{ marginBottom: 20 }}>
            <Loading />
          </View>
        ) : (
          <></>
        )}
      </NoticeLayout>
    );
  }

  return (
    <ScrollView
      showsVerticalScrollIndicator={false}
      scrollEnabled={isScrollingEnabled}
      nestedScrollEnabled={true}
    >
      <View style={styles.container}>
        <PageTitle
          title="Notices"
          icon={require('../../assets/notices.png')}
          haveGoBackButton={true}
        />
      </View>
      {isLoading === false && notice ? (
        <View style={styles.container}>
          <View
            style={{
              paddingHorizontal: 10,
              paddingVertical: 15,
              borderColor: STYLE_VARS.fccDarkGrey,
              borderWidth: 0.5,
              marginVertical: 30,
            }}
          >
            <View style={{}}>
              <Text style={styles.title}>{notice?.title}</Text>
            </View>
            <View style={styles.horizontalLine} />
            <View style={{ marginBottom: 20 }}>
              <Text style={styles.dayText}>
                {dayjs(new Date(notice.date * 1000)).format('DD-MM-YYYY')}
              </Text>
            </View>
            <View style={{}}>
              <Text style={styles.dayText}>
                <RenderHTML
                  contentWidth={width - 80}
                  source={{ html: notice.content }}
                  tagsStyles={{
                    a: {
                      fontSize: 13,
                    },
                  }}
                />
              </Text>
            </View>
            {notice.document && (
              <>
                <View
                  style={{
                    marginVertical: 10,
                  }}
                >
                  {IS_WEB ? (
                    <iframe
                      src={`${notice.document.url}#view=FitH&toolbar=0&background=0&statusbar=0&messages=0&navpanes=0`}
                      style={{
                        height: 410,
                      }}
                      frameBorder={0}
                      typeof={'application/pdf'}
                    ></iframe>
                  ) : (
                    <View
                      onTouchStart={setIsScrollingTrue}
                      onTouchEnd={setIsScrollingFalse}
                      onTouchCancel={setIsScrollingFalse}
                    >
                      {/* Disable on website */}
                      {/* <Pdf
                        source={{ uri: notice.document.url, cache: true }}
                        style={{
                          width: '100%',
                          height: 410,
                        }}
                        trustAllCerts={false}
                        // horizontal={IS_ANDROID ? true : false}
                      /> */}
                    </View>
                  )}
                </View>
                <View
                  style={{
                    alignSelf: 'flex-start',
                    marginVertical: 20,
                    flexDirection: 'row',
                    alignItems: 'center',
                    gap: 20,
                  }}
                >
                  <Button
                    text="DOWNLOAD PDF"
                    style={STYLE_VARS.blueButton}
                    onPress={() => {
                      Linking.openURL(notice.document.url);
                    }}
                  />
                </View>
              </>
            )}
          </View>
        </View>
      ) : isLoading === true ? (
        <View style={{ marginBottom: 20 }}>
          <Loading />
        </View>
      ) : (
        <></>
      )}
      <Footer />
    </ScrollView>
  );
};

export default NoticeDetail;

const styles = StyleSheet.create({
  container: {
    paddingHorizontal: 20,
  },
  title: {
    fontFamily: FONTS.AvenirMedium,
    fontSize: 18,
    fontWeight: '500',
    color: STYLE_VARS.textColor,
    textTransform: 'uppercase',
  },
  dayText: {
    fontFamily: FONTS.AvenirMedium,
    fontSize: 13,
    fontWeight: '500',
    color: STYLE_VARS.fccDarkerGrey,
    textTransform: 'uppercase',
  },
  horizontalLine: {
    width: 30,
    height: 3,
    backgroundColor: STYLE_VARS.primaryColor,
    marginVertical: 10,
  },
  borderDarkGrey: {
    borderColor: STYLE_VARS.fccDarkGrey,
    borderWidth: 0.5,
  },
  titleText: {
    fontFamily: FONTS.AvenirMedium,
    fontWeight: '500',
    color: STYLE_VARS.textColor,
    textTransform: 'uppercase',
    letterSpacing: 1,
    marginBottom: 10,
  },
  titleSpan: {
    width: 30,
    height: 3,
    backgroundColor: STYLE_VARS.primaryColor,
    marginBottom: 15,
  },
  dateText: {
    fontFamily: FONTS.AvenirMedium,
    fontSize: 15,
    color: STYLE_VARS.fccDarkerGrey,
    marginBottom: 15,
    textTransform: 'uppercase',
    letterSpacing: 1,
  },
  contentText: {
    fontFamily: FONTS.AvenirMedium,
    fontSize: 15,
    color: STYLE_VARS.fccDarkerGrey,
    marginBottom: 25,
    textTransform: 'uppercase',
  },
});
